import { Injectable } from "@angular/core";
import { ConfigServiceValue } from "@fabric-types/svc-config";
import { PlatformServiceCommand, PlatformServiceValue } from "@fabric-types/svc-platform";
import { Store } from "@ngrx/store";
import { ObgConfigFeAdaptiveClientCore } from "@obg/core.config";
import { ConfigService, DocumentRef, ignoreNil } from "@obg/ngx.global";
import { PlatformService } from "@obg/ngx.global.evo";
import { RouteSelector, RouterState } from "@obg/ngx.router";
import { Observable, combineLatest, of, switchMap } from "rxjs";
import { filter, map, take, tap } from "rxjs/operators";

import { AuthSelector } from "../auth/auth.selector";
import { Jurisdiction } from "../jurisdiction/jurisdiction.model";
import { JurisdictionSelector } from "../jurisdiction/jurisdiction.selector";

const CTA_MODAL_SHOWN_STORAGE_KEY = "core.ctaModal.shown";

@Injectable({
	providedIn: "root",
})
export class CtaModalService {
	constructor(
		private store: Store<RouterState>,
		private configService: ConfigService<ObgConfigFeAdaptiveClientCore>,
		private platformService: PlatformService,
		private documentRef: DocumentRef
	) {}

	currentEnabledRoute$(): Observable<string | undefined> {
		if (this.platformService.isServer) {
			return of(undefined);
		}

		const routesEnabled = this.configService.get((config) => config?.coreUi?.root?.ctaModal?.enabledRoutes, []);

		return combineLatest([
			this.store.select(JurisdictionSelector.get),
			this.store.select(AuthSelector.isAuthenticated),
			this.store.select(RouteSelector.getCurrent).pipe(ignoreNil()),
		]).pipe(
			filter(([jurisdiction]) => !!jurisdiction),
			switchMap(([jurisdiction, isAuthenticated, route]) => {
				if (
					isAuthenticated ||
					sessionStorage.getItem(CTA_MODAL_SHOWN_STORAGE_KEY) === "true" ||
					!routesEnabled.includes(route.name)
				) {
					return of(undefined);
				}

				if (jurisdiction === Jurisdiction.COGA) {
					return combineLatest([
						new Observable(this.documentRef.obgEventBus.retrieve(ConfigServiceValue.DeviceType)),
						new Observable(this.documentRef.obgEventBus.retrieve(PlatformServiceValue.ExitIntent)),
					]).pipe(
						filter(([deviceType, exitIntentDetected]) => !!deviceType && exitIntentDetected),
						take(1),
						map(([deviceType]) => {
							if (deviceType === "Mobile") {
								this.documentRef.obgEventBus?.dispatch(PlatformServiceCommand.ScrollToTop, 0);
							}
							return route.name;
						})
					);
				}

				return of(route.name);
			}),
			tap((routeName) => {
				if (routeName) {
					sessionStorage.setItem(CTA_MODAL_SHOWN_STORAGE_KEY, "true");
				}
			})
		);
	}
}
